/**
 * ActivityTab — Timeline of business activity events.
 *
 * Shows chronological events: business added, creative generated,
 * proposal sent/accepted, campaign launched, etc.
 */
import React from 'react';
import {
  Building2, Video, CheckCircle2, FileText, Send, Rocket, Clock,
} from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { formatRelativeTime } from '@/components/leads/mockData';

const ICON_MAP = {
  business_added: Building2,
  creative_generated: Video,
  creative_approved: CheckCircle2,
  proposal_drafted: FileText,
  proposal_sent: Send,
  proposal_accepted: CheckCircle2,
  campaign_launched: Rocket,
};

const COLOR_MAP = {
  business_added: 'bg-slate-100 text-slate-600',
  creative_generated: 'bg-blue-100 text-blue-600',
  creative_approved: 'bg-emerald-100 text-emerald-600',
  proposal_drafted: 'bg-slate-100 text-slate-600',
  proposal_sent: 'bg-blue-100 text-blue-600',
  proposal_accepted: 'bg-emerald-100 text-emerald-600',
  campaign_launched: 'bg-coral/10 text-coral',
};

export default function ActivityTab({ activities = [] }) {
  if (activities.length === 0) {
    return (
      <EmptyState
        icon={Clock}
        title="No activity yet"
        description="Activity will appear as you work with this business."
      />
    );
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-[19px] top-2 bottom-2 w-px bg-border" />

      <div className="space-y-0">
        {activities.map((event, idx) => {
          const Icon = ICON_MAP[event.type] || Clock;
          const colorClass = COLOR_MAP[event.type] || 'bg-muted text-muted-foreground';

          return (
            <div key={event.id} className="relative flex items-start gap-4 py-3">
              {/* Icon dot */}
              <div className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full shrink-0 ${colorClass}`}>
                <Icon className="h-4 w-4" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pt-1.5">
                <p className="text-sm font-medium text-foreground">{event.label}</p>
                {event.detail && (
                  <p className="text-xs text-muted-foreground mt-0.5">{event.detail}</p>
                )}
                <p className="text-[10px] text-muted-foreground mt-1">
                  {formatRelativeTime(event.timestamp)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
